import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import SyncWizard from 'components/SyncWizard';
import PageHeader from 'components/PageHeader';
import buildRoute from 'components/buildRoute';

// Import step components
import CrosstabsStep1 from './steps/CrosstabsStep1';
import CrosstabsStep2 from './steps/CrosstabsStep2';
import CrosstabsStep3and4 from './steps/CrosstabsStep3and4';
import CrosstabsStep5 from './steps/CrosstabsStep5';
import CrosstabsStep6 from './steps/CrosstabsStep6';
import { trackEngagementActivity } from '../../configureMixpanel';

const NewCrosstabPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Step width configuration - easy to customize per step
  const getStepWidth = (stepIndex) => {
    const stepWidths = {
      0: 800,  // Step 1: Select Data Source
      1: 800,  // Step 2: Map Source Data  
      2: 1400, // Step 3: Select Rows
      3: 1400, // Step 4: Select Columns
      4: 1400, // Step 5: Review Settings 
      5: 800   // Step 6: Generate Crosstab
    };
    return stepWidths[stepIndex] || 800; 
  };
  
  // Step management - derive from URL
  const getCurrentStepFromUrl = () => {
    const path = location.pathname;
    if (path.includes('/step1')) return 0;
    if (path.includes('/step2')) return 1;
    if (path.includes('/step3')) return 2;
    if (path.includes('/step4')) return 3;
    if (path.includes('/step5')) return 4;
    if (path.includes('/step6')) return 5;
    return 0;
  };
  
  const [activeStep, setActiveStep] = useState(getCurrentStepFromUrl());
  
  // Step 1 data
  const [crosstabName, setCrosstabName] = useState('');
  
  // Flag to prevent saving to session storage until initial load is complete
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [surveyResponseTable, setSurveyResponseTable] = useState(null);
  const [questionLabelsTable, setQuestionLabelsTable] = useState(null);
  const [responseLabelsTable, setResponseLabelsTable] = useState(null);
  
  // Step 2 data
  const [questionIdField, setQuestionIdField] = useState('');
  const [questionTextField, setQuestionTextField] = useState('');
  const [questionTypeField, setQuestionTypeField] = useState('');
  const [parentQuestionIdField, setParentQuestionIdField] = useState('');
  const [optionTextField, setOptionTextField] = useState('');
  const [responseField, setResponseField] = useState('');
  const [weightKeyField, setWeightKeyField] = useState('');
  
  // Step 3 data (Row Selection)
  const [selectedRowQuestions, setSelectedRowQuestions] = useState([]);
  
  // Step 4 data (Column Selection)  
  const [selectedColumnQuestions, setSelectedColumnQuestions] = useState([]);
  
  // Legacy selectedQuestions for backward compatibility (combines rows + columns)
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  
  // Step 5 data 
  const [responseOrders, setResponseOrders] = useState({});
  const [hiddenResponses, setHiddenResponses] = useState({});
  const [collapses, setCollapses] = useState({});
  
  // Step 5 formatted output for crosstabs submission
  const [formattedCrosstabsData, setFormattedCrosstabsData] = useState(null);

  // Update activeStep when URL changes
  useEffect(() => {
    setActiveStep(getCurrentStepFromUrl());
  }, [location.pathname]);

  useEffect(() => {
    trackEngagementActivity("new_crosstab_wizard_opened");
  }, []);

  // Load form data from sessionStorage on mount
  useEffect(() => {
    const savedData = sessionStorage.getItem('crosstabFormData');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        
        // Load form fields
        setCrosstabName(data.crosstabName || '');
        setQuestionIdField(data.questionIdField || '');
        setQuestionTextField(data.questionTextField || '');
        setQuestionTypeField(data.questionTypeField || '');
        setParentQuestionIdField(data.parentQuestionIdField || '');
        setOptionTextField(data.optionTextField || '');
        setResponseField(data.responseField || '');
        setWeightKeyField(data.weightKeyField || '');
        
        // Load row and column questions (with backward compatibility)
        setSelectedRowQuestions(data.selectedRowQuestions || []);
        setSelectedColumnQuestions(data.selectedColumnQuestions || []);
        // Legacy support: if old selectedQuestions exists but no row/column split, use it for both
        if (data.selectedQuestions && !data.selectedRowQuestions && !data.selectedColumnQuestions) {
          setSelectedRowQuestions(data.selectedQuestions);
          setSelectedColumnQuestions(data.selectedQuestions);
        }
        // Store combined 
        setSelectedQuestions([...(data.selectedRowQuestions || []), ...(data.selectedColumnQuestions || [])]);
        setResponseOrders(data.responseOrders || {});
        setHiddenResponses(data.hiddenResponses || {});
        setCollapses(data.collapses || {});
        
        if (data.selectedDataset) {
          setSelectedDataset(data.selectedDataset);
          setTimeout(() => {
            setSurveyResponseTable(data.surveyResponseTable || null);
            setQuestionLabelsTable(data.questionLabelsTable || null);
            setResponseLabelsTable(data.responseLabelsTable || null);
            setQuestionIdField(data.questionIdField || '');
            setQuestionTextField(data.questionTextField || '');
            setQuestionTypeField(data.questionTypeField || '');
            setParentQuestionIdField(data.parentQuestionIdField || '');
            setOptionTextField(data.optionTextField || '');
            setResponseField(data.responseField || '');
            setWeightKeyField(data.weightKeyField || '');
          }, 500);
        }
        } catch (error) {
          console.warn('Error loading saved form data:', error);
          sessionStorage.removeItem('crosstabFormData');
        }
      }
      
      // Mark initial load as complete
      setIsInitialLoadComplete(true);
    }, []);

  // Save form data to sessionStorage whenever it changes (but only after initial load)
  useEffect(() => {
    if (!isInitialLoadComplete) {
      return;
    }
    
    const formData = {
      crosstabName,
      selectedDataset,
      surveyResponseTable,
      questionLabelsTable,
      responseLabelsTable,
      questionIdField,
      questionTextField,
      questionTypeField,
      parentQuestionIdField,
      optionTextField,
      responseField,
      weightKeyField,
      selectedRowQuestions,
      selectedColumnQuestions,
      selectedQuestions, 
      responseOrders,
      hiddenResponses,
      collapses,
    };
    sessionStorage.setItem('crosstabFormData', JSON.stringify(formData));
  }, [isInitialLoadComplete, crosstabName, selectedDataset, surveyResponseTable, questionLabelsTable, responseLabelsTable, 
      questionIdField, questionTextField, questionTypeField, parentQuestionIdField, optionTextField, responseField, weightKeyField, 
      selectedRowQuestions, selectedColumnQuestions, selectedQuestions, responseOrders, hiddenResponses, collapses]);
  
  // Keep selectedQuestions in sync with row and column questions
  useEffect(() => {
    const combinedQuestions = [...new Set([...selectedRowQuestions, ...selectedColumnQuestions])];
    setSelectedQuestions(combinedQuestions);
  }, [selectedRowQuestions, selectedColumnQuestions]);

  // Validation state
  const [showErrors, setShowErrors] = useState(false);

  const clearFormData = () => {
    sessionStorage.removeItem('crosstabFormData');
  };




  // Step validation functions
  const isStep1Valid = () => {
    return crosstabName.trim() !== '' &&
           selectedDataset &&
           surveyResponseTable &&
           questionLabelsTable &&
           responseLabelsTable;
  };

  const isStep2Valid = () => {
    return questionIdField && 
           questionTextField && 
           questionTypeField &&
           optionTextField && 
           responseField;
  };

  const isStep3Valid = () => {
    return selectedRowQuestions.length > 0;
  };

  const isStep4Valid = () => {
    return selectedColumnQuestions.length > 0;
  };

  const handleNext = () => {
    if (activeStep === 0 && !isStep1Valid()) {
      setShowErrors(true);
      return;
    }
    
    if (activeStep === 1 && !isStep2Valid()) {
      setShowErrors(true);
      return;
    }
    
    if (activeStep === 2 && !isStep3Valid()) {
      setShowErrors(true);
      return;
    }
    
    if (activeStep === 3 && !isStep4Valid()) {
      setShowErrors(true);
      return;
    }
    
    setShowErrors(false);
    const nextStep = activeStep + 1;
    const stepUrls = ['step1', 'step2', 'step3', 'step4', 'step5', 'step6'];
    if (nextStep < stepUrls.length) {
      navigate(`/projects/${projectId}/crosstabs/new/${stepUrls[nextStep]}`);
    }
  };

  const handleBack = () => {
    setShowErrors(false);
    const prevStep = activeStep - 1;
    const stepUrls = ['step1', 'step2', 'step3', 'step4', 'step5', 'step6'];
    if (prevStep >= 0) {
      navigate(`/projects/${projectId}/crosstabs/new/${stepUrls[prevStep]}`);
    }
  };

  // Step 1 content
  const Step1_SelectDataSource = (
    <CrosstabsStep1
      projectId={projectId}
      crosstabName={crosstabName}
      setCrosstabName={setCrosstabName}
      selectedDataset={selectedDataset}
      setSelectedDataset={setSelectedDataset}
      surveyResponseTable={surveyResponseTable}
      setSurveyResponseTable={setSurveyResponseTable}
      questionLabelsTable={questionLabelsTable}
      setQuestionLabelsTable={setQuestionLabelsTable}
      responseLabelsTable={responseLabelsTable}
      setResponseLabelsTable={setResponseLabelsTable}
      showErrors={showErrors}
      onNext={handleNext}
    />
  );

  // Step 2 content
  const Step2_MapSourceData = (
    <CrosstabsStep2
      projectId={projectId}
      selectedDataset={selectedDataset}
      surveyResponseTable={surveyResponseTable}
      questionLabelsTable={questionLabelsTable}
      responseLabelsTable={responseLabelsTable}
      questionIdField={questionIdField}
      setQuestionIdField={setQuestionIdField}
      questionTextField={questionTextField}
      setQuestionTextField={setQuestionTextField}
      questionTypeField={questionTypeField}
      setQuestionTypeField={setQuestionTypeField}
      parentQuestionIdField={parentQuestionIdField}
      setParentQuestionIdField={setParentQuestionIdField}
      optionTextField={optionTextField}
      setOptionTextField={setOptionTextField}
      responseField={responseField}
      setResponseField={setResponseField}
      weightKeyField={weightKeyField}
      setWeightKeyField={setWeightKeyField}
      showErrors={showErrors}
      onNext={handleNext}
      onBack={handleBack}
    />
  );

  // Step 3 content - Row Selection
  const Step3_SelectRows = (
    <CrosstabsStep3and4
      projectId={projectId}
      selectedDataset={selectedDataset}
      questionLabelsTable={questionLabelsTable}
      questionIdField={questionIdField}
      questionTextField={questionTextField}
      questionTypeField={questionTypeField}
      parentQuestionIdField={parentQuestionIdField}
      selectedQuestions={selectedRowQuestions}
      setSelectedQuestions={setSelectedRowQuestions}
      stepTitle="Select Row Questions"
      stepDescription="Choose the questions that will appear as rows in your crosstab."
      nextButtonText="Next: Select Column Questions"
      showErrors={showErrors}
      onNext={handleNext}
      onBack={handleBack}
    />
  );

  // Step 4 content - Column Selection
  const Step4_SelectColumns = (
    <CrosstabsStep3and4
      projectId={projectId}
      selectedDataset={selectedDataset}
      questionLabelsTable={questionLabelsTable}
      questionIdField={questionIdField}
      questionTextField={questionTextField}
      questionTypeField={questionTypeField}
      parentQuestionIdField={parentQuestionIdField}
      selectedQuestions={selectedColumnQuestions}
      setSelectedQuestions={setSelectedColumnQuestions}
      stepTitle="Select Column Questions"
      stepDescription="Choose the questions that will appear as columns in your crosstab."
      nextButtonText="Next: Order Responses"
      showErrors={showErrors}
      onNext={handleNext}
      onBack={handleBack}
    />
  );

  // Memoize the combined questions array to prevent unnecessary re-renders of Step 5
  const allSelectedQuestions = useMemo(() => {
    return [...selectedRowQuestions, ...selectedColumnQuestions];
  }, [selectedRowQuestions, selectedColumnQuestions]);

  // Step 5 content - Response Options (formerly Step 4)
  const Step5_OrderResponses = (
    <CrosstabsStep5
      projectId={projectId}
      selectedDataset={selectedDataset}
      surveyResponseTable={surveyResponseTable}
      responseLabelsTable={responseLabelsTable}
      questionIdField={questionIdField}
      optionTextField={optionTextField}
      responseField={responseField}
      parentQuestionIdField={parentQuestionIdField}
      weightKeyField={weightKeyField}
      selectedQuestions={allSelectedQuestions}
      selectedRowQuestions={selectedRowQuestions}
      selectedColumnQuestions={selectedColumnQuestions}
      responseOrders={responseOrders}
      setResponseOrders={setResponseOrders}
      hiddenResponses={hiddenResponses}
      setHiddenResponses={setHiddenResponses}
      collapses={collapses}
      setCollapses={setCollapses}
      formattedCrosstabsData={formattedCrosstabsData}
      setFormattedCrosstabsData={setFormattedCrosstabsData}
      showErrors={showErrors}
      onNext={handleNext}
      onBack={handleBack}
    />
  );



  // Define wizard steps
  const steps = [
    {
      label: "Select Data Source",
      content: Step1_SelectDataSource
    },
    {
      label: "Map Source Data",
      content: Step2_MapSourceData
    },
    {
      label: "Select Rows",
      content: Step3_SelectRows
    },
    {
      label: "Select Columns",
      content: Step4_SelectColumns
    },
    {
      label: "Order Responses", 
      content: Step5_OrderResponses
    },
    {
      label: "Generate Crosstab",
      content: (
        <CrosstabsStep6
          // Step 1 data
          selectedDataset={selectedDataset}
          surveyResponseTable={surveyResponseTable}
          questionLabelsTable={questionLabelsTable}
          responseLabelsTable={responseLabelsTable}
          crosstabName={crosstabName}
          // Step 2 data
          questionIdField={questionIdField}
          optionTextField={optionTextField}
          responseField={responseField}
          parentQuestionIdField={parentQuestionIdField}
          weightKeyField={weightKeyField}
          // Step 5 data
          formattedCrosstabsData={formattedCrosstabsData}
        />
      )
    }
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <PageHeader
        title="Create a new crosstab"
        subtitle="Generate cross-tabulation reports from your survey data."
      />
      <SyncWizard
        steps={steps}
        activeStepIndex={activeStep}
        disableStepClick={true}
        hideStepLabels={true}
        maxWidth={getStepWidth(activeStep)}
      />
    </Box>
  );
};

const routeArgs = {
  element: <NewCrosstabPage />,
};

export default buildRoute(routeArgs); 